import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Trash, X, CircleNotch, Warning } from "@phosphor-icons/react";

export default function DeleteReportDialog({ report, onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!password.trim() || busy) return;
    setBusy(true);
    try {
      await api.delete(`/reports/${report.id}`, { data: { password } });
      toast.success("Report deleted");
      onDeleted(report.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="report-delete-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={busy ? undefined : onClose} />
      <div className="relative w-full max-w-md rounded-sm border border-neutral-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#DC2626]/10"><Trash size={17} weight="bold" className="text-[#DC2626]" /></div>
            <h2 className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">Delete report</h2>
          </div>
          <button data-testid="report-delete-cancel-x" onClick={onClose} disabled={busy} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-50"><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4 p-5">
          <div className="flex gap-2 rounded-sm bg-[#DC2626]/5 px-3 py-2.5 text-sm text-neutral-700">
            <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-[#DC2626]" />
            <span>You're about to permanently delete <span className="font-semibold">"{report.title}"</span>. This cannot be undone.</span>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">Confirm your account password</label>
            <input data-testid="report-delete-password" type="password" autoFocus value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="Your password"
              className="w-full rounded-sm border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#DC2626]" />
            <p className="mt-1 text-xs text-neutral-400">For your security, deleting a report requires re-entering your password.</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button data-testid="report-delete-cancel" onClick={onClose} disabled={busy} className="rounded-sm border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">Cancel</button>
          <button data-testid="report-delete-confirm" onClick={submit} disabled={busy || !password.trim()}
            className="inline-flex items-center gap-2 rounded-sm bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B91C1C] disabled:opacity-50">
            {busy ? <><CircleNotch size={15} className="animate-spin" /> Deleting…</> : <><Trash size={15} weight="bold" /> Delete report</>}
          </button>
        </div>
      </div>
    </div>
  );
}
